import React, { useRef, useMemo, useEffect, memo } from 'react';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import { OrbitControls, Points, PointMaterial } from '@react-three/drei';
import { useInView } from 'framer-motion';

// ============================================================================
// CONFIGURATION CONSTANTS
// ============================================================================

// Particle system
const PARTICLE_COUNT = 1500; // Reduced from 3000 for mobile/low-end devices
const PARTICLE_SIZE = 0.02;
const PARTICLE_OPACITY = 0.6;
const PARTICLE_MIN_RADIUS = 4;
const PARTICLE_MAX_RADIUS = 12;

// Rotation speeds (radians per frame)
const ICOSAHEDRON_ROTATION_X = 0.002;
const ICOSAHEDRON_ROTATION_Y = 0.003;
const ORBITING_SHAPE_ROTATION = 0.01;
const PARTICLE_ROTATION_Y = 0.0005;
const PARTICLE_ROTATION_X = 0.0002;

// Camera settings
const CAMERA_POSITION = [0, 0, 8];
const CAMERA_FOV = 60;

// Colors
const COLOR_CYAN = '#00f0ff';
const COLOR_MAGENTA = '#ff00aa';
const COLOR_PURPLE = '#8b5cf6';
const COLOR_WHITE = '#ffffff';

// Geometry sizes
const ICOSAHEDRON_RADIUS = 2.5;
const ICOSAHEDRON_DETAIL = 1;
const ORBITING_SHAPE_SIZE = 0.3;

// Auto-rotate
const AUTO_ROTATE_SPEED = 0.5;

// ============================================================================
// COMPONENTS
// ============================================================================

// Wireframe Icosahedron (center) with proper cleanup
function Icosahedron() {
  const meshRef = useRef();
  const geometryRef = useRef();
  const materialRef = useRef();

  useEffect(() => {
    return () => {
      // Dispose geometry and material on unmount
      geometryRef.current?.dispose();
      materialRef.current?.dispose();
    };
  }, []);

  useFrame(() => {
    if (meshRef.current) {
      meshRef.current.rotation.x += ICOSAHEDRON_ROTATION_X;
      meshRef.current.rotation.y += ICOSAHEDRON_ROTATION_Y;
    }
  });

  return (
    <mesh ref={meshRef}>
      <icosahedronGeometry ref={geometryRef} args={[ICOSAHEDRON_RADIUS, ICOSAHEDRON_DETAIL]} />
      <meshBasicMaterial
        ref={materialRef}
        color={COLOR_CYAN}
        wireframe
        transparent
        opacity={0.3}
      />
    </mesh>
  );
}

// Orbiting shapes with proper cleanup
function OrbitingShape({ geometry, position, speed, color }) {
  const meshRef = useRef();
  const geometryRef = useRef();
  const materialRef = useRef();
  const initialAngle = useRef(Math.random() * Math.PI * 2);

  useEffect(() => {
    return () => {
      geometryRef.current?.dispose();
      materialRef.current?.dispose();
    };
  }, []);

  useFrame((state) => {
    if (meshRef.current) {
      const time = state.clock.elapsedTime * speed;
      const radius = position[0];
      meshRef.current.position.x = Math.cos(time + initialAngle.current) * radius;
      meshRef.current.position.z = Math.sin(time + initialAngle.current) * radius;
      meshRef.current.position.y = position[1] + Math.sin(time * 2) * 0.5;
      meshRef.current.rotation.x += ORBITING_SHAPE_ROTATION;
      meshRef.current.rotation.y += ORBITING_SHAPE_ROTATION;
    }
  });

  const GeometryComponent = () => {
    switch (geometry) {
      case 'octahedron':
        return <octahedronGeometry ref={geometryRef} args={[ORBITING_SHAPE_SIZE]} />;
      case 'tetrahedron':
        return <tetrahedronGeometry ref={geometryRef} args={[ORBITING_SHAPE_SIZE]} />;
      case 'box':
        return <boxGeometry ref={geometryRef} args={[ORBITING_SHAPE_SIZE, ORBITING_SHAPE_SIZE, ORBITING_SHAPE_SIZE]} />;
      default:
        return <boxGeometry ref={geometryRef} args={[ORBITING_SHAPE_SIZE, ORBITING_SHAPE_SIZE, ORBITING_SHAPE_SIZE]} />;
    }
  };

  return (
    <mesh ref={meshRef} position={position}>
      <GeometryComponent />
      <meshBasicMaterial ref={materialRef} color={color} wireframe transparent opacity={0.6} />
    </mesh>
  );
}

// Particle system with proper cleanup
function Particles({ count = PARTICLE_COUNT }) {
  const pointsRef = useRef();
  const materialRef = useRef();

  const positions = useMemo(() => {
    const pos = new Float32Array(count * 3);
    for (let i = 0; i < count; i++) {
      const radius = PARTICLE_MIN_RADIUS + Math.random() * (PARTICLE_MAX_RADIUS - PARTICLE_MIN_RADIUS);
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.acos(2 * Math.random() - 1);
      pos[i * 3] = radius * Math.sin(phi) * Math.cos(theta);
      pos[i * 3 + 1] = radius * Math.sin(phi) * Math.sin(theta);
      pos[i * 3 + 2] = radius * Math.cos(phi);
    }
    return pos;
  }, [count]);

  useEffect(() => {
    return () => {
      materialRef.current?.dispose();
    };
  }, []);

  useFrame(() => {
    if (pointsRef.current) {
      pointsRef.current.rotation.y += PARTICLE_ROTATION_Y;
      pointsRef.current.rotation.x += PARTICLE_ROTATION_X;
    }
  });

  return (
    <Points ref={pointsRef} positions={positions} stride={3} frustumCulled={false}>
      <PointMaterial
        ref={materialRef}
        transparent
        color={COLOR_WHITE}
        size={PARTICLE_SIZE}
        sizeAttenuation={true}
        depthWrite={false}
        opacity={PARTICLE_OPACITY}
      />
    </Points>
  );
}

// Cleanup helper for the entire scene
function SceneCleanup() {
  const { gl, scene } = useThree();

  useEffect(() => {
    return () => {
      // Dispose all scene resources on unmount
      scene.traverse((object) => {
        if (object.geometry) {
          object.geometry.dispose();
        }
        if (object.material) {
          if (Array.isArray(object.material)) {
            object.material.forEach((mat) => mat.dispose());
          } else {
            object.material.dispose();
          }
        }
      });
      gl.dispose();
    };
  }, [gl, scene]);

  return null;
}

// Main Scene - wrapped in React.memo to prevent unnecessary re-renders
const Scene3D = memo(function Scene3D({ className }) {
  const containerRef = useRef(null);
  const isInView = useInView(containerRef, { margin: '100px' });

  return (
    <div ref={containerRef} className={`canvas-container ${className || ''}`}>
      <Canvas
        camera={{ position: CAMERA_POSITION, fov: CAMERA_FOV }}
        dpr={[1, 1.5]}
        frameloop={isInView ? 'always' : 'never'}
      >
        {/* Scene cleanup on unmount */}
        <SceneCleanup />

        {/* Lighting */}
        <ambientLight intensity={0.2} />
        <pointLight position={[10, 10, 10]} color={COLOR_CYAN} intensity={0.5} />
        <pointLight position={[-10, -10, -10]} color={COLOR_MAGENTA} intensity={0.3} />

        {/* Particles (stars) only */}
        <Particles count={PARTICLE_COUNT} />

        {/* Drag controls */}
        <OrbitControls
          enableZoom={false}
          enablePan={false}
          autoRotate
          autoRotateSpeed={AUTO_ROTATE_SPEED}
          maxPolarAngle={Math.PI}
          minPolarAngle={0}
        />
      </Canvas>
    </div>
  );
});

export default Scene3D;
